/**
 * Kleda Client App - API Functions
 * Communicate with Vercel serverless functions
 */

import type {
  OrderResponse,
  KiesAfspraakRequest,
  KiesAfspraakResponse,
  WijzigAfspraakRequest,
  WijzigAfspraakResponse,
  AnnuleerRequest,
  AnnuleerResponse,
  ReactiveerRequest,
  ReactiveerResponse,
  OpmerkingenRequest,
  OpmerkingenResponse,
  PricingBootstrap,
  AddVloerkleedRequest,
  AddVloerkleedResponse,
  EditVloerkleedRequest,
  EditVloerkleedResponse,
  DeleteVloerkleedRequest,
  DeleteVloerkleedResponse,
  UpdateAdresRequest,
  UpdateAdresResponse,
  UpdateTelefoonRequest,
  UpdateTelefoonResponse,
  RandReparatieRequest,
  RandReparatieResponse,
  OndertapijtRequest,
  OndertapijtResponse,
  OndertapijtNabestelResponse,
  VlekkenbaasRequest,
  VlekkenbaasResponse
} from './types'

const API_BASE = '/api'

// =============================================================================
// ORDER DATA
// =============================================================================

/**
 * Haal order data op via token.
 *
 * @param checkMollie — als true, laat Salesforce een live Mollie API check doen
 * als de cached betaalstatus nog 'open' is. Gebruik dit alleen tijdens de
 * payment-polling window (ná een klik op "Nu betalen"), niet bij initial load.
 */
export async function fetchOrder(
  token: string,
  includeExtraRoutes = false,
  checkMollie = false
): Promise<OrderResponse> {
  try {
    const params = new URLSearchParams({ token })
    if (includeExtraRoutes) {
      params.set('extraRoutes', 'true')
    }
    if (checkMollie) {
      params.set('checkMollie', 'true')
    }

    const response = await fetch(`${API_BASE}/client-app?${params}`)
    const data = await response.json()

    // API returns { success, data?, error? } structure
    return data as OrderResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

// =============================================================================
// KIES AFSPRAAK
// =============================================================================

/**
 * Kies een ophaal- of aflevermoment
 */
export async function kiesAfspraak(
  request: KiesAfspraakRequest
): Promise<KiesAfspraakResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/kies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as KiesAfspraakResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

// =============================================================================
// WIJZIG AFSPRAAK
// =============================================================================

/**
 * Wijzig een bestaande afspraak
 */
export async function wijzigAfspraak(
  request: WijzigAfspraakRequest
): Promise<WijzigAfspraakResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/wijzig`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as WijzigAfspraakResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

// =============================================================================
// ANNULEER
// =============================================================================

/**
 * Annuleer een order
 */
export async function annuleerOrder(
  request: AnnuleerRequest
): Promise<AnnuleerResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/annuleer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as AnnuleerResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

// =============================================================================
// REACTIVEER ("toch laten reinigen")
// =============================================================================

/**
 * Heropen een geannuleerde order. Salesforce zet de WO terug naar
 * 'Ophalen plannen'; daarna laadt de app de order opnieuw en valt de klant
 * direct in de kies-ophaalmoment-flow.
 */
export async function reactiveerOrder(
  request: ReactiveerRequest
): Promise<ReactiveerResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/reactiveer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as ReactiveerResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

// =============================================================================
// UPDATE OPMERKINGEN
// =============================================================================

/**
 * Update opmerkingen voor ophalen/afleveren
 */
export async function updateOpmerkingen(
  request: OpmerkingenRequest
): Promise<OpmerkingenResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/opmerkingen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as OpmerkingenResponse
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kan geen verbinding maken met de server.',
      },
    }
  }
}

/**
 * Antwoord op "we zijn eerder dan gepland" (plan 2026-10-12): ja | nee | plek.
 */
export async function stuurVroegAkkoord(request: {
  token: string
  kant: 'ophalen' | 'afleveren'
  stand: 'ja' | 'nee' | 'plek'
  plek?: string
}): Promise<{ success: boolean; error?: { code: string; message: string } }> {
  try {
    const response = await fetch(`${API_BASE}/client-app/vroeg-akkoord`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return (await response.json()) as { success: boolean; error?: { code: string; message: string } }
  } catch {
    return { success: false, error: { code: 'NETWORK_ERROR', message: 'Kan geen verbinding maken met de server.' } }
  }
}

// =============================================================================
// VOLGENDE REINIGING (plan 2026-10-22, herzien 2026-10-26)
// =============================================================================

export interface TerugkomOptie {
  label: string
  vanaf: string
  tot: string
}

/** Waar een keuze vandaan kwam; zelfde lijst als de office-route (volgende-reiniging.ts). */
export type VolgendeKeerBron = 'client-app' | 'aflever-fase' | 'mail-link' | 's10-chip' | 's10-whatsapp' | 'care-guide' | 'mijn-kleda'

export interface AbonnementStand {
  id: string
  intervalMaanden: number
  nextRunAt: string
  seizoenLabel: string | null
  /** false = de vorige cyclus was al overgeslagen; nog een keer = stoppen. */
  overslaanMag: boolean
  /** Laatste dag om nog vóór de order over te slaan of te stoppen (YYYY-MM-DD). */
  overslaanTot: string
  kortingPct: number
}

/** Startseizoen voor een vast ritme: rond basis + interval (server rekent), naam zonder jaartal voor de knop. */
export interface RitmeStartOptie {
  label: string
  naam: string
  vanaf: string
  tot: string
  standaard: boolean
}

export interface TerugkomStand {
  /** Mag de klant op deze order kiezen (na aflevering, of in de aflever-fase). */
  afgeleverd: boolean
  toegestaan?: boolean
  opties: TerugkomOptie[]
  /** Per interval (6, 12, 18, 24) de kiesbare startseizoenen voor een vast ritme. */
  ritmeOpties?: Record<string, RitmeStartOptie[]>
  gekozen: { seizoen: string | null; vanaf: string; tot: string; kanaalVoorkeur: string | null; bronOrder: boolean } | null
  abonnement: AbonnementStand | null
}

type ApiUitkomst<T> = { success: boolean; data?: T; error?: { code: string; message: string } }
const NETWERKFOUT = { success: false, error: { code: 'NETWORK_ERROR', message: 'Kan geen verbinding maken met de server.' } }

export async function fetchVolgendeReiniging(token: string, bron: VolgendeKeerBron = 'client-app'): Promise<ApiUitkomst<TerugkomStand>> {
  try {
    const params = new URLSearchParams({ token, bron })
    const response = await fetch(`${API_BASE}/client-app/terugkom?${params}`)
    return (await response.json()) as ApiUitkomst<TerugkomStand>
  } catch {
    return NETWERKFOUT
  }
}

export async function zetTerugkom(request: {
  token: string
  seizoen?: string
  redenKlant?: string
  kanaalVoorkeur?: 'mail' | 'whatsapp'
  bron?: VolgendeKeerBron
  /** 'wissen' = toch zelf beslissen; 'zelf' = alleen meten dat de klant "Ik beslis zelf" koos. */
  actie?: 'wissen' | 'zelf'
}): Promise<ApiUitkomst<{ gekozen: { seizoen: string; vanaf: string; tot: string } | null; vervangen: boolean; gewist: number }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/terugkom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return (await response.json()) as ApiUitkomst<{ gekozen: { seizoen: string; vanaf: string; tot: string } | null; vervangen: boolean; gewist: number }>
  } catch {
    return NETWERKFOUT
  }
}

export async function maakKleedAbonnement(request: {
  token: string
  intervalMaanden: number
  seizoen?: string
  kanaalVoorkeur?: 'mail' | 'whatsapp'
  bron?: VolgendeKeerBron
}): Promise<ApiUitkomst<{ id: string; intervalMaanden: number; nextRunAt: string; seizoen: string | null; kortingPct: number | null; overslaanTot?: string | null }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/abonnement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return (await response.json()) as ApiUitkomst<{ id: string; intervalMaanden: number; nextRunAt: string; seizoen: string | null; kortingPct: number | null; overslaanTot?: string | null }>
  } catch {
    return NETWERKFOUT
  }
}

// =============================================================================
// MIJN KLEDA (plan 2026-10-26, golf C)
// =============================================================================

export interface MijnKledaBestelling {
  nummer: string
  datum: string | null
  status: string
  statusLabel: string
  fase: 'open' | 'afgerond' | 'geannuleerd'
  aantalKleden: number | null
  isHuidig: boolean
  /** Detail op te vragen (golf 3, alleen ingelogd). */
  kanDetail?: boolean
}

/** basis = via de link in een mail (ordertoken); volledig = ingelogd met een inloglink. */
export type MijnKledaNiveau = 'basis' | 'volledig'

export interface MijnKledaData {
  niveau: MijnKledaNiveau
  klant: { voornaam: string | null; naam: string | null; email: string | null; telefoon: string | null; adres: string | null }
  kleden: Array<{ omschrijving: string }>
  richtprijs: { bedrag: number; pakketLabel: string; omschrijving: string } | null
  bestelOpnieuwMogelijk: boolean
  bestellingen: MijnKledaBestelling[]
  volgendeReiniging: {
    terugkom: { id: string; seizoen: string | null; vanaf: string; tot: string; kanaalVoorkeur: string | null; bronWoSfId: string } | null
    abonnement: AbonnementStand | null
  }
  /** aan: null = niet in de marketingbasis (zakelijk, of nieuw en nog niet gesynct); dan geen schakelaar. */
  mailvoorkeur: { aan: boolean } | { aan: null; reden: 'niet_in_marketingbasis' }
  /** Golf 2 (plan 2026-10-28). Ontbreekt bij een oude office-versie: dan alleen-lezen. */
  gegevens?: MijnKledaGegevens
  reviewLink: string
}

export interface MijnKledaAdres {
  straat: string | null
  toevoeging: string | null
  postcode: string | null
  plaats: string | null
}

export interface MijnKledaGegevens {
  /** Gegevens wijzigen staat aan; false = alleen-lezen zoals voorheen. */
  wijzigen: boolean
  mobiel: string | null
  /** Adres van deze bestelling dat hier nog te wijzigen is; null = de bestelling is voorbij plannen. */
  orderAdres: (MijnKledaAdres & { kant: 'ophalen' | 'afleveren' }) | null
  /** Adres op de klantkaart; alleen ingelogd. */
  klantkaartAdres: MijnKledaAdres | null
  /** E-mail wijzigen mag (ingelogd, bevestigingsmail staat aan). */
  emailWijzigen: boolean
  /** Nieuw adres dat op de klik in de bevestigingsmail wacht. */
  emailInBehandeling: string | null
}

export type MijnKledaActieUitkomst =
  | { ok: true; actie: 'overslaan'; uitkomst: 'overgeslagen' | 'gestopt'; nextRunAt: string | null }
  | { ok: true; actie: 'stop' }
  | { ok: true; actie: 'bestel-opnieuw'; url: string; soort: 'offerte' | 'client-app'; nieuw: boolean }
  | { ok: true; actie: 'mailvoorkeur'; aan: boolean }
  | { ok: true; actie: 'telefoon'; mobiel: string }
  | { ok: true; actie: 'adres'; scope: 'order' | 'klantkaart'; uitkomst: 'gewijzigd' | 'kantoor'; melding: string }
  | { ok: true; actie: 'email-start'; nieuwEmail: string }

export async function fetchMijnKleda(token: string): Promise<ApiUitkomst<MijnKledaData>> {
  try {
    const params = new URLSearchParams({ token })
    const response = await fetch(`${API_BASE}/client-app/mijn-kleda?${params}`)
    return (await response.json()) as ApiUitkomst<MijnKledaData>
  } catch {
    return NETWERKFOUT
  }
}

// Inloggen (plan 2026-10-28, golf 1). De sessie zit in een httpOnly-cookie
// op dit domein; fetch stuurt hem vanzelf mee (credentials same-origin).

export async function vraagInlogAan(email: string, bron: string): Promise<ApiUitkomst<{ melding: string }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/inlog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, bron }),
    })
    return (await response.json()) as ApiUitkomst<{ melding: string }>
  } catch {
    return NETWERKFOUT
  }
}

export async function wisselInlogIn(t: string): Promise<ApiUitkomst<{ orderToken: string }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/inlog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t }),
    })
    return (await response.json()) as ApiUitkomst<{ orderToken: string }>
  } catch {
    return NETWERKFOUT
  }
}

export async function uitloggen(): Promise<ApiUitkomst<{ uitgelogd: boolean }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/inlog`, { method: 'DELETE' })
    return (await response.json()) as ApiUitkomst<{ uitgelogd: boolean }>
  } catch {
    return NETWERKFOUT
  }
}

export async function postMijnKleda(request: {
  token: string
  actie: 'overslaan' | 'stop' | 'bestel-opnieuw' | 'mailvoorkeur' | 'telefoon' | 'adres' | 'email-start'
  aan?: boolean
  mobiel?: string
  scope?: 'order' | 'klantkaart'
  straat?: string
  toevoeging?: string
  postcode?: string
  plaats?: string
  email?: string
}): Promise<ApiUitkomst<MijnKledaActieUitkomst>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/mijn-kleda`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return (await response.json()) as ApiUitkomst<MijnKledaActieUitkomst>
  } catch {
    return NETWERKFOUT
  }
}

/** De klik in mail MK02 (golf 2): bevestigt het nieuwe e-mailadres. */
export async function bevestigEmail(t: string): Promise<ApiUitkomst<{ nieuwEmail: string; orderToken: string | null }>> {
  try {
    const response = await fetch(`${API_BASE}/client-app/inlog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailBevestig: t }),
    })
    return (await response.json()) as ApiUitkomst<{ nieuwEmail: string; orderToken: string | null }>
  } catch {
    return NETWERKFOUT
  }
}

// =============================================================================
// PRICING - Voor vloerkleed toevoegen
// =============================================================================

/**
 * Haal pricing bootstrap data op (packages, kortingen, etc.)
 */
export async function fetchPricingBootstrap(token: string): Promise<PricingBootstrap> {
  try {
    const params = new URLSearchParams({ token })
    const response = await fetch(`${API_BASE}/client-app/pricing?${params}`)
    const data = await response.json()
    return data as PricingBootstrap
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
      packages: [],
      bundleDiscounts: [],
      regioFactor: 1,
      minimumOrderExBtw: 0,
      basePricePerM2: 0,
      bestaandeAantalKleden: 0,
      bestaandeTotaalM2: 0,
      kanToevoegen: false
    }
  }
}

// =============================================================================
// ADD VLOERKLEED
// =============================================================================

/**
 * Voeg een nieuw vloerkleed toe aan de order
 */
export async function addVloerkleed(
  request: AddVloerkleedRequest
): Promise<AddVloerkleedResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/add-vloerkleed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    return data as AddVloerkleedResponse
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// EDIT VLOERKLEED
// =============================================================================

/**
 * Wijzig maten/vorm/pakket van een bestaand vloerkleed op de order
 */
export async function editVloerkleed(
  request: EditVloerkleedRequest
): Promise<EditVloerkleedResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/edit-vloerkleed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    // Proxy envelope: { success, data? , error? } → platslaan naar EditVloerkleedResponse
    if (data.success) {
      return { success: true, ...(data.data || {}) }
    }
    return {
      success: false,
      errorCode: data.error?.code || data.errorCode,
      message: data.error?.message || data.message || 'Er ging iets mis',
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// DELETE VLOERKLEED
// =============================================================================

/**
 * Verwijder een vloerkleed (incl. pakket en gekoppelde extra's) van de order
 */
export async function deleteVloerkleed(
  request: DeleteVloerkleedRequest
): Promise<DeleteVloerkleedResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/delete-vloerkleed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    if (data.success) {
      return { success: true, ...(data.data || {}) }
    }
    return {
      success: false,
      errorCode: data.error?.code || data.errorCode,
      message: data.error?.message || data.message || 'Er ging iets mis',
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// VOORINSPECTIE THUMBNAILS
// =============================================================================

// Media-laag fase 2: thumbnails uit de eigen media-tabel i.p.v. CompanyCam.
// Flag uit (of eigen media leeg, bv. pre-fase-1-kleden) = oude pad.
const MEDIA_THUMBS_EIGEN = import.meta.env.VITE_MEDIA_THUMBS_EIGEN === 'true'

export interface VoorinspectieMedia {
  /** itemId → thumbnail image-url */
  thumbnails: Record<string, string>
  /** itemId → eigen /fotos/{token}-pagina; leeg op het CompanyCam-pad */
  fotoLinks: Record<string, string>
}

const LEGE_MEDIA: VoorinspectieMedia = { thumbnails: {}, fotoLinks: {} }

async function fetchThumbnailsFrom(endpoint: string, token: string): Promise<VoorinspectieMedia> {
  // Werpt nooit: een kapotte bron mag de andere niet meetrekken in de merge.
  try {
    const params = new URLSearchParams({ token })
    const response = await fetch(`${API_BASE}/client-app/${endpoint}?${params}`)
    if (!response.ok) return LEGE_MEDIA
    const data = await response.json()
    const result: VoorinspectieMedia = { thumbnails: {}, fotoLinks: {} }
    for (const item of data.data?.items || []) {
      if (!item.itemId) continue
      if (item.thumbnailUrl) result.thumbnails[item.itemId] = item.thumbnailUrl
      if (item.fotosUrl) result.fotoLinks[item.itemId] = item.fotosUrl
    }
    return result
  } catch {
    return LEGE_MEDIA
  }
}

/**
 * Haal per kleed de thumbnail en de eigen foto-timeline-link op.
 * Faalt stil met lege maps zodat het rapport nooit blokkeert.
 *
 * Bronnen worden PER KLEED gemengd, niet of/of: een kleed kan wél eigen media
 * hebben (bv. tijdens-foto's van vandaag) maar geen eigen voorinspectie-foto,
 * omdat de voorinspectie van vóór de media-laag in CompanyCam zit. Eigen wint;
 * CompanyCam vult de gaten. Bij de CompanyCam-afbouw levert dat pad gewoon
 * niets meer en blijft alleen eigen over.
 */
export async function fetchVoorinspectieThumbnails(token: string): Promise<VoorinspectieMedia> {
  try {
    if (!MEDIA_THUMBS_EIGEN) {
      return await fetchThumbnailsFrom('voorinspectie-thumbnails', token)
    }
    const [eigen, companycam] = await Promise.all([
      fetchThumbnailsFrom('media-thumbnails', token),
      fetchThumbnailsFrom('voorinspectie-thumbnails', token),
    ])
    return {
      thumbnails: { ...companycam.thumbnails, ...eigen.thumbnails },
      fotoLinks: eigen.fotoLinks,
    }
  } catch {
    return LEGE_MEDIA
  }
}

// =============================================================================
// ADRES LOOKUP (PDOK)
// =============================================================================

export interface AdresLookupResult {
  straat: string
  plaats: string
}

/**
 * Zoek straat + plaats op via postcode + huisnummer (PDOK Locatieserver)
 */
export async function lookupAdres(postcode: string, huisnummer: string): Promise<AdresLookupResult | null> {
  try {
    const params = new URLSearchParams({ pc: postcode, hn: huisnummer })
    const response = await fetch(`${API_BASE}/client-app/adres-lookup?${params}`)
    if (!response.ok) return null
    const data = await response.json()
    if (data.ok && data.street && data.city) {
      return { straat: data.street, plaats: data.city }
    }
    return null
  } catch {
    return null
  }
}

// =============================================================================
// UPDATE ADRES
// =============================================================================

/**
 * Wijzig het ophaal- of afleveradres (kant volgt uit de orderfase).
 * Na succes de order opnieuw laden: route-opties worden live uit de
 * postcode berekend en passen zich dus automatisch aan de nieuwe regio aan.
 */
export async function updateAdres(
  request: UpdateAdresRequest
): Promise<UpdateAdresResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/update-adres`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    if (data.success) {
      return { success: true, ...(data.data || {}) }
    }
    return {
      success: false,
      errorCode: data.error?.code || data.errorCode,
      message: data.error?.message || data.message || 'Er ging iets mis',
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// UPDATE TELEFOON
// =============================================================================

/**
 * Wijzig het mobiele nummer van de klant. Salesforce schrijft naar de juiste
 * bron (PersonMobilePhone voor particulier, Contact.MobilePhone voor B2B).
 */
export async function updateTelefoon(
  request: UpdateTelefoonRequest
): Promise<UpdateTelefoonResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/update-telefoon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()
    if (data.success) {
      return { success: true, ...(data.data || {}) }
    }
    return {
      success: false,
      errorCode: data.error?.code || data.errorCode,
      message: data.error?.message || data.message || 'Er ging iets mis',
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// RAND REPARATIE UPSELL
// =============================================================================

/**
 * Bevestig of weiger rand reparatie upsell
 */
export async function confirmRandReparatie(
  request: RandReparatieRequest
): Promise<RandReparatieResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/rand-reparatie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()

    // Handle API wrapper response
    if (data.success === false && data.error) {
      return {
        success: false,
        errorCode: data.error.code,
        message: data.error.message
      }
    }

    return {
      success: data.success,
      message: data.data?.message,
      alreadyOrdered: data.data?.alreadyOrdered
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// ONDERTAPIJT UPSELL
// =============================================================================

/**
 * Bevestig of weiger ondertapijt upsell
 */
export async function confirmOndertapijt(
  request: OndertapijtRequest
): Promise<OndertapijtResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/ondertapijt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()

    // Handle API wrapper response
    if (data.success === false && data.error) {
      return {
        success: false,
        errorCode: data.error.code,
        message: data.error.message
      }
    }

    return {
      success: data.success,
      message: data.data?.message,
      alreadyOrdered: data.data?.alreadyOrdered
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

/**
 * Nabestel ondertapijt ná aflevering (after-flow). Maakt een nieuwe
 * webshop-order met aanbiedingsprijs + verzendkosten en geeft de
 * Mollie-betaallink terug.
 */
export async function nabestelOndertapijt(
  token: string,
  itemIds: string[]
): Promise<OndertapijtNabestelResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/ondertapijt-nabestel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, itemIds }),
    })

    const data = await response.json()

    if (data.success === false && data.error) {
      return {
        success: false,
        errorCode: data.error.code,
        message: data.error.message
      }
    }

    return {
      success: data.success,
      werkOrderName: data.data?.werkOrderName ?? null,
      paymentUrl: data.data?.paymentUrl ?? null,
      subtotalCents: data.data?.subtotalCents ?? null,
      shippingCents: data.data?.shippingCents ?? null,
      chargedCents: data.data?.chargedCents ?? null,
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// VLEKKENBAAS UPSELL
// =============================================================================

/**
 * Bevestig vlekkenbaas upsell
 */
export async function confirmVlekkenbaas(
  request: VlekkenbaasRequest
): Promise<VlekkenbaasResponse> {
  try {
    const response = await fetch(`${API_BASE}/client-app/vlekkenbaas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    const data = await response.json()

    if (data.success === false && data.error) {
      return {
        success: false,
        errorCode: data.error.code,
        message: data.error.message
      }
    }

    return {
      success: data.success,
      message: data.data?.message,
      alreadyOrdered: data.data?.alreadyOrdered
    }
  } catch {
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      message: 'Kan geen verbinding maken met de server.',
    }
  }
}

// =============================================================================
// BETAALLINK
// =============================================================================

export interface RefreshPaymentLinkResult {
  betaalLink?: string
  betaalStatus?: string
  openstaandBedrag?: number
}

/**
 * Regenereer synchroon de Mollie-betaallink zodat het bedrag klopt vóórdat de
 * klant kan betalen. Aan te roepen direct na een upsell die het openstaand
 * bedrag verhoogt (vlekkenbaas / ondertapijt / randreparatie); de trigger doet
 * dit ook maar asynchroon, waardoor de klant anders de oude link met het te
 * lage bedrag betaalt.
 *
 * Best-effort: gooit nooit. Bij falen blijft het async trigger-vangnet over en
 * geeft deze functie `null` terug — de aanroeper gaat gewoon door met refetch.
 */
export async function refreshPaymentLink(
  token: string
): Promise<RefreshPaymentLinkResult | null> {
  try {
    const response = await fetch(`${API_BASE}/client-app/payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })

    const data = await response.json()

    // Ook bij een nette fout kan er een fallback-link in data zitten.
    if (data?.data?.betaalLink || data?.success) {
      return {
        betaalLink: data.data?.betaalLink,
        betaalStatus: data.data?.betaalStatus,
        openstaandBedrag: data.data?.openstaandBedrag,
      }
    }
    return null
  } catch {
    return null
  }
}
